import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Megaphone, Wallet, TrendingUp, Users, AlertTriangle,
  BarChart3, ExternalLink, Clock,
} from "lucide-react";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";
import { useCabangOptions } from "@/lib/cabang";
import { PageHeader, TidakPunyaAkses, adaField, useCampaignMap } from "./_shared";
import AiAsisten from "./AiAsisten";

export default function MarketingDashboard() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_dashboard", "view");
  const bolehCampaign = can("marketing_campaign", "view");
  const bolehKol = can("marketing_kol", "view");

  const [campaignFilter, setCampaignFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const brandOpsi = useCabangOptions({ denganKosong: true });
  const { campaignMap } = useCampaignMap(bolehCampaign);

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "dashboard-overview", campaignFilter],
    () => api.get("/marketing/dashboard/overview", {
      params: campaignFilter ? { campaign_id: campaignFilter } : {},
    }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  const { data: campaignAktif, loading: loadingCampaign } = useDataQuery(
    ["marketing", "campaigns-aktif", brandFilter],
    () => api.get("/marketing/campaigns", {
      params: { status: "approved", ...(brandFilter ? { brand: brandFilter } : {}) },
    }).then((r) => r.data),
    { enabled: bolehCampaign },
  );

  const { data: dealsList } = useDataQuery(
    ["marketing", "kol-deals-semua"],
    () => api.get("/marketing/kol/deals").then((r) => r.data),
    { enabled: bolehKol },
  );
  const { data: deliverablesLate, loading: loadingLate } = useDataQuery(
    ["marketing", "kol-deliverables-late"],
    () => api.get("/marketing/kol/deliverables", { params: { status: "late" } }).then((r) => r.data),
    { enabled: bolehKol },
  );
  const dealMap = useMemo(() => {
    const m = {};
    (dealsList || []).forEach((d) => { m[d.id] = d; });
    return m;
  }, [dealsList]);

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={LayoutDashboard} title="Dashboard Marketing" />
        <TidakPunyaAkses label="Dashboard Marketing" testid="mkt-dash-tanpa-akses" />
      </div>
    );
  }

  const spend = data?.spend || { ads_digital: 0, kol_endorsement: 0, promosi_media: 0, total: 0 };
  const maxSpendChannel = Math.max(1, spend.ads_digital, spend.kol_endorsement, spend.promosi_media);
  const perStage = data?.lead?.per_stage || {};
  const maxStage = Math.max(1, ...Object.values(perStage));
  const marginAda = adaField(data, "margin");
  const roiAda = adaField(data, "roi");

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-dashboard">
      <PageHeader
        icon={LayoutDashboard}
        title="Dashboard Marketing"
        desc="Ringkasan spend, ROAS, funnel lead, dan deliverable KOL — Fase 1."
        actions={
          bolehCampaign && (
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}
                    data-testid="mkt-dash-filter-campaign"
                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white text-slate-700">
              <option value="">Semua Campaign</option>
              {Object.values(campaignMap).map((c) => (
                <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>
              ))}
            </select>
          )
        }
      />

      {/* AI Marketing Assistant (brief owner 31 Agu 2026) — komponennya sendiri
          yang menyembunyikan diri untuk akun tanpa izin AI (lihat AiAsisten.jsx),
          jadi tidak ada gerbang kedua yang bisa berpisah jalan di sini. */}
      <AiAsisten />

      <LoadingState loading={loading} error={error} onRetry={refetch} varian="kartu" jumlahKartu={4} data-testid="mkt-dash-loading">
        {data && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard icon={Wallet} label="Total Spend" value={idr(spend.total)} testid="mkt-dash-kpi-spend" />
              <KpiCard icon={TrendingUp} label="Omzet Teratribusi" value={idr(data.omzet)} testid="mkt-dash-kpi-omzet" />
              <KpiCard icon={BarChart3} label="ROAS" value={data.roas != null ? `${data.roas.toFixed(2)}x` : "-"} testid="mkt-dash-kpi-roas" />
              {marginAda && roiAda ? (
                <KpiCard icon={TrendingUp} label="ROI" value={data.roi != null ? `${(data.roi * 100).toFixed(1)}%` : "-"}
                         testid="mkt-dash-kpi-roi" aksen="emerald" />
              ) : (
                <KpiCard icon={Users} label="Lead Masuk" value={fmtNum(data.lead?.total)} testid="mkt-dash-kpi-lead" />
              )}
            </div>
            <p className="text-xs text-slate-400 -mt-2">
              Total akumulasi seluruh campaign yang tercatat{campaignFilter ? " untuk campaign terpilih" : ""}.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Spend per channel */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-dash-spend-channel">
                <p className="text-sm font-semibold text-slate-900 mb-3">Spend per Channel</p>
                <div className="space-y-3">
                  <BarSpend label="Iklan Digital" value={spend.ads_digital} max={maxSpendChannel} warna="bg-sky-500" />
                  <BarSpend label="Endorsement & KOL" value={spend.kol_endorsement} max={maxSpendChannel} warna="bg-violet-500" />
                  <BarSpend label="Promosi Media (PFP)" value={spend.promosi_media} max={maxSpendChannel} warna="bg-amber-500" />
                </div>
              </div>

              {/* Funnel lead */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 lg:col-span-2" data-testid="mkt-dash-funnel">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-900">Funnel Lead</p>
                  {data.lead?.stale_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                      <AlertTriangle className="w-3 h-3" /> {data.lead.stale_count} lead mengendap &gt;48 jam
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {["new", "followup", "qualified", "negotiation", "closed_won", "closed_lost"].map((stage) => (
                    <FunnelStage key={stage} stage={stage} value={perStage[stage] || 0} max={maxStage} />
                  ))}
                </div>
              </div>
            </div>

            {/* Top campaign by ROAS */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-dash-top-roas">
              <div className="p-4 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-900">Top Campaign berdasarkan ROAS</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr className="text-left">
                      <th className="px-3 py-2.5 font-semibold">Campaign</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Spend</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Omzet</th>
                      <th className="px-3 py-2.5 font-semibold text-right">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.top_campaigns_by_roas || []).length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">Belum ada campaign dengan omzet teratribusi.</td></tr>
                    )}
                    {(data.top_campaigns_by_roas || []).map((c) => (
                      <tr key={c.campaign_id} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2.5">
                          <Link to={`/marketing/campaigns/${c.campaign_id}`} className="text-emerald-700 hover:underline font-medium">
                            {c.code} — {c.nama}
                          </Link>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono">{idr(c.spend)}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{idr(c.omzet)}</td>
                        <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-700">{c.roas.toFixed(2)}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </LoadingState>

      {/* Campaign aktif */}
      {bolehCampaign && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-dash-campaign-aktif">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
              <Megaphone className="w-4 h-4 text-emerald-600" /> Campaign Aktif ({(campaignAktif || []).length})
            </p>
            <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} data-testid="mkt-dash-filter-brand"
                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white text-slate-700">
              <option value="">Semua Brand</option>
              {brandOpsi.filter(Boolean).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <th className="px-3 py-2.5 font-semibold">Kode / Nama</th>
                  <th className="px-3 py-2.5 font-semibold">Brand / Cabang</th>
                  <th className="px-3 py-2.5 font-semibold">Periode</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Budget</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <LoadingState loading={loadingCampaign} kosong={!loadingCampaign && (campaignAktif || []).length === 0}
                              varian="tabel" kolom={5} pesanKosong="Belum ada campaign berstatus disetujui."
                              data-testid="mkt-dash-campaign-aktif-state">
                  {(campaignAktif || []).map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`mkt-dash-campaign-row-${c.id}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-mono text-[11px] text-slate-500">{c.code}</p>
                        <p className="text-slate-900 font-medium">{c.nama}</p>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{c.brand || "-"}{c.cabang ? ` / ${c.cabang}` : ""}</td>
                      <td className="px-3 py-2.5 text-slate-600">{formatTanggal(c.tanggal_mulai)} – {formatTanggal(c.tanggal_selesai)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{idr(c.budget_plan)}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Link to={`/marketing/campaigns/${c.id}`} data-testid={`mkt-dash-campaign-lihat-${c.id}`}
                              className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-xs font-medium">
                          Detail <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </LoadingState>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alert deliverable telat */}
      {bolehKol && (
        <div className="bg-white border border-rose-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-dash-deliverable-telat">
          <div className="p-4 border-b border-rose-100 bg-rose-50/50">
            <p className="text-sm font-semibold text-rose-800 inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> Deliverable KOL Telat ({(deliverablesLate || []).length})
            </p>
            <p className="text-xs text-rose-700 mt-0.5">Sudah lewat tenggat dan belum disubmit — pelunasan deal terkait otomatis terkunci.</p>
          </div>
          <ul className="divide-y divide-slate-100">
            <LoadingState loading={loadingLate} kosong={!loadingLate && (deliverablesLate || []).length === 0}
                          pesanKosong="Tidak ada deliverable yang telat."
                          data-testid="mkt-dash-deliverable-telat-state">
              {(deliverablesLate || []).map((dl) => {
                const deal = dealMap[dl.deal_id];
                return (
                  <li key={dl.id} className="p-3.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`mkt-dash-late-row-${dl.id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {deal ? `${deal.kol_nama} — ${deal.code}` : (dl.kol_id || "").slice(0, 8)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {DELIVERABLE_LABEL(dl.jenis)}{dl.deskripsi ? `: ${dl.deskripsi}` : ""} · Tenggat {formatTanggal(dl.due_date)}
                      </p>
                    </div>
                    {deal && (
                      <Link to={`/marketing/kol/${deal.kol_id}`} className="text-xs font-medium text-emerald-700 hover:underline inline-flex items-center gap-1">
                        Lihat KOL <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </li>
                );
              })}
            </LoadingState>
          </ul>
        </div>
      )}
    </div>
  );
}

function DELIVERABLE_LABEL(jenis) {
  const m = { post_feed: "Post Feed", story: "Story", reels: "Reels", video: "Video", live: "Live", lainnya: "Lainnya" };
  return m[jenis] || jenis;
}

function KpiCard({ icon: Icon, label, value, testid, aksen = "slate" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${aksen === "emerald" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-600"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-bold text-slate-900 mt-0.5 font-mono">{value}</p>
    </div>
  );
}

function BarSpend({ label, value, max, warna }) {
  const pct = Math.max(2, Math.round((value / max) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-mono font-medium text-slate-800">{idr(value)}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${warna}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function FunnelStage({ stage, value, max }) {
  const label = { new: "Baru", followup: "Follow Up", qualified: "Qualified", negotiation: "Negosiasi", closed_won: "Deal (Menang)", closed_lost: "Batal (Kalah)" }[stage];
  const pct = Math.max(4, Math.round((value / max) * 100));
  const warna = stage === "closed_won" ? "bg-emerald-500" : stage === "closed_lost" ? "bg-rose-400" : "bg-sky-400";
  return (
    <div className="border border-slate-100 rounded-lg p-2.5">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-lg font-bold text-slate-900 font-mono">{value}</p>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
        <div className={`h-full rounded-full ${warna}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
